import { Box, Text } from "@mantine/core";
import * as _ from "lodash-es";
import {
  createRow,
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_SortingState,
  type MRT_TableOptions,
  useMantineReactTable
} from "mantine-react-table-open";
import { useEffect, useMemo, useState } from "react";
import type { ApiResponse } from "../../../../api";
import { usePerson } from "../../../../hooks/usePerson";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { convertApiErrorsToMrtErrors } from "../../../../mantine/mrt/utils/convertApiErrorsToMrtErrors";
import { isMrtCreating } from "../../../../mantine/mrt/utils/isMrtCreating";
import { useToast } from "../../../../mantine/notifications/useToast";
import { getWeightingColumn } from "../../../../modules/Paragraph6/Paragraph6ContractPage/Paragraph6ContractInformation/columns/getWeightingColumn";
import { THEME_VARS } from "../../../../utils/constants";
import { getErrorText } from "../../../../utils/get-error-text";
import { AnchorLink } from "../../../AnchorLink/AnchorLink";
import { MultiConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { getGeneratorRedispatchColumn } from "../../../ComponentListContainer/ComponentList/columns/getGeneratorRedispatchColum";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import type {
  DirektvermarktungGeneratorShareFormValues,
  DirektvermarktungGeneratorShareResponse,
  DirektvermarktungUnassignedGeneratorsResponse
} from "../../Direktvermarktung.types";
import { useDirektvermarktungUnassignedGenerators } from "../../hooks/useDirektvermarktungUnassignedGenerators";
import type { RedispatchStatus } from "../../utils/getDVRedispatchStatus";
import { getDVRedispatchStatus } from "../../utils/getDVRedispatchStatus";
import { getGeneratorColumn } from "./Columns/getGeneratorColumn";
import { getMaStRColumn } from "./Columns/getMaStRColumn";
import { getZaehlpunktColumn } from "./Columns/getZaehlpunktColumn";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "generator_display_name",
    desc: false
  }
];

interface DirektvermarktungGeneratorShareTableProps {
  generatorShares: Array<DirektvermarktungGeneratorShareResponse>;
  contractUuid: string;
  operatorId: number;
  onDelete: (generatorShareId: number) => Promise<void>;
  onSubmit: (
    updatedGenerator: DirektvermarktungGeneratorShareFormValues,
    generatorShareId?: number
  ) => Promise<ApiResponse>;
}

function DirektvermarktungGeneratorShareTable({
  generatorShares,
  contractUuid,
  operatorId,
  onDelete,
  onSubmit
}: DirektvermarktungGeneratorShareTableProps) {
  const { showToast, showErrorToast } = useToast();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [generatorSharesToBeDeleted, setGeneratorSharesToBeDeleted] = useState<
    Array<DirektvermarktungGeneratorShareResponse>
  >([]);

  const [currentlySelectedGenerator, setCurrentlySelectedGenerator] = useState<
    DirektvermarktungUnassignedGeneratorsResponse | undefined
  >(undefined);

  const [redispatchStatusBeforeSave, setRedispatchStatusBeforeSave] =
    useState<RedispatchStatus>(undefined);

  const {
    data: person,
    isLoading: isLoadingPerson,
    error: personError
  } = usePerson(operatorId);

  const isRedispatchImportActive = person?.isRedispatchImportActive || false;

  const {
    data: direktvermarktungUnassignedGenerators,
    isLoading,
    error
  } = useDirektvermarktungUnassignedGenerators({
    contractUuid
  });

  const currentRedispatchStatus = getDVRedispatchStatus(generatorShares);

  const columns = useMemo<
    Array<MRT_ColumnDef<DirektvermarktungGeneratorShareResponse>>
  >(
    () => [
      getGeneratorColumn({
        validationErrors: validationErrors,
        unassignedGenerators: direktvermarktungUnassignedGenerators,
        onChange: (
          generator: DirektvermarktungUnassignedGeneratorsResponse
        ) => {
          setCurrentlySelectedGenerator(generator);
        }
      }),
      getMaStRColumn(),
      getGeneratorRedispatchColumn({
        generatorId: currentlySelectedGenerator?.generator_id,
        siteId: currentlySelectedGenerator?.site_id
      }),
      getZaehlpunktColumn(),
      getWeightingColumn({
        key: "weighting",
        siteIdKey: "site_id",
        options: {
          header: "Aufteilungsfaktor"
        }
      })
    ],
    [
      direktvermarktungUnassignedGenerators,
      currentlySelectedGenerator,
      validationErrors
    ]
  );

  const firstUnassignedGenerator = useMemo(() => {
    if (!direktvermarktungUnassignedGenerators?.length) {
      return undefined;
    }

    return direktvermarktungUnassignedGenerators[0];
  }, [direktvermarktungUnassignedGenerators]);

  const handleCreateGenerator: MRT_TableOptions<DirektvermarktungGeneratorShareResponse>["onCreatingRowSave"] =
    async function ({ exitCreatingMode, row }) {
      const isMeteringOrMarketLocationMissing =
        row.original.metering_or_market_location_id === null;
      const isWeightingMissing =
        row.original.weighting === null || row.original.weighting === "";

      if (isMeteringOrMarketLocationMissing || isWeightingMissing) {
        showErrorToast(
          new Error(
            "Speichern nicht möglich: Zählpunkt und Aufteilungsfaktor müssen befüllt sein."
          )
        );
        return;
      }

      // Capture status before save
      setRedispatchStatusBeforeSave(currentRedispatchStatus);
      await createGenerator(row.original, exitCreatingMode);
    };

  async function createGenerator(
    values: MRT_Row<DirektvermarktungGeneratorShareResponse>["original"],
    exitCreatingMode: () => void
  ) {
    setIsSaving(true);
    setValidationErrors({});

    const updatedGenerator: DirektvermarktungGeneratorShareFormValues = {
      generator_id: values.generator_id,
      metering_or_market_location_id: values.metering_or_market_location_id
    };

    try {
      await onSubmit(updatedGenerator);
    } catch (error) {
      if (error.response.status === 400) {
        const validationErrors = convertApiErrorsToMrtErrors(
          error.response.data
        );

        if (Object.keys(validationErrors).length > 0) {
          setValidationErrors(validationErrors);
        } else {
          showErrorToast(error);
        }

        setIsSaving(false);
        return;
      } else {
        showErrorToast(error);
      }
    }

    exitCreatingMode();
    setIsSaving(false);
    setCurrentlySelectedGenerator(undefined);
  }

  // Check if redispatch status changed from uniform to mixed after save
  useEffect(() => {
    if (
      redispatchStatusBeforeSave !== undefined &&
      redispatchStatusBeforeSave !== "mixed" &&
      currentRedispatchStatus === "mixed"
    ) {
      showToast({
        type: "warning",
        autoClose: false,
        withCloseButton: true,
        message: (
          <Box>
            <Text>
              Durch das Mischen von Erzeugern mit und ohne das Add-On{" "}
              <strong>
                &quot;Automatischer Datenimport für Redispatch- und
                DV-Abregelungsenergiemengen&quot;
              </strong>{" "}
              basieren die Ergebnisse im Erlösmonitoring sowohl auf{" "}
              <strong>automatisch</strong> als auch auf <strong>manuell</strong>{" "}
              importierten Daten.
            </Text>
            <Text>
              Ist dies nicht gewünscht, wenden Sie sich gerne an{" "}
              <AnchorLink href="mailto:sales@node.energy" target="_self">
                sales@node.energy
              </AnchorLink>
              , um fehlende Lizenzen nachzubuchen.
            </Text>
          </Box>
        )
      });
      setRedispatchStatusBeforeSave(undefined);
    }
  }, [currentRedispatchStatus, redispatchStatusBeforeSave, showToast]);

  const defaultOptions =
    getDefaultMRTOptions<DirektvermarktungGeneratorShareResponse>({
      emptyRowsFallbackText: "Fügen Sie den ersten Erzeuger hinzu.",
      searchTextInput: {
        placeholder: "Suche nach Erzeugern"
      },
      initialState: {
        sorting: DEFAULT_SORTED
      },
      hideBorder: true
    });

  const rowEditModeOptions =
    getDefaultMRTRowEditModeOptions<DirektvermarktungGeneratorShareResponse>({
      rowActions: {
        delete: {
          text: "Erzeuger aus dem Direktvermarktungsvertrag entfernen",
          onClick: (row) => setGeneratorSharesToBeDeleted([row.original])
        }
      }
    });

  const tableOptions = _.merge({}, defaultOptions, rowEditModeOptions, {
    ...getMRTTopToolbarCustomActionsRenderer<DirektvermarktungGeneratorShareResponse>(
      {
        create: {
          text: "Erzeuger hinzufügen",
          disabled:
            !direktvermarktungUnassignedGenerators ||
            direktvermarktungUnassignedGenerators.length === 0
        },
        delete: {
          text: "Erzeuger entfernen",
          onClick: () => {
            const generationShares = table
              .getSelectedRowModel()
              .rows.map((row) => row.original)
              .filter((generationShare) => generationShare.weighting !== "");
            setGeneratorSharesToBeDeleted(generationShares);
          }
        }
      }
    ),
    enablePagination: false,
    enableToolbarInternalActions: false,
    enableBottomToolbar: false,
    displayColumnDefOptions: {
      "mrt-row-select": {
        visibleInShowHideMenu: false,
        size: 40,
        minSize: 40
      },
      "mrt-row-actions": {
        visibleInShowHideMenu: false,
        size: 40,
        minSize: 40
      }
    },
    mantineToolbarAlertBannerProps: error
      ? {
          color: THEME_VARS.error[6],
          children: getErrorText(error)
        }
      : undefined,
    state: {
      isLoading: isLoading,
      isSaving: isSaving || isDeleting,
      columnVisibility: {
        isRedispatchImportActive
      }
    },
    onCreatingRowCancel: () => {
      setCurrentlySelectedGenerator(undefined);
      setValidationErrors({});
    },
    onCreatingRowSave: handleCreateGenerator
  });

  const table = useMantineReactTable<DirektvermarktungGeneratorShareResponse>({
    ...tableOptions,
    columns,
    data: generatorShares
  });

  const tableIsCreating = isMrtCreating(table);

  useEffect(() => {
    if (
      !tableIsCreating ||
      currentlySelectedGenerator ||
      !firstUnassignedGenerator
    ) {
      return;
    }

    setCurrentlySelectedGenerator(firstUnassignedGenerator);
  }, [tableIsCreating, currentlySelectedGenerator, firstUnassignedGenerator]);

  useEffect(() => {
    if (!tableIsCreating || !currentlySelectedGenerator) {
      return;
    }

    const creatingRow = table.getState().creatingRow;
    if (!creatingRow) {
      return;
    }

    const prefilledCreatingRow =
      createRow<DirektvermarktungGeneratorShareResponse>(table, {
        ...creatingRow.original,
        generator_display_name:
          currentlySelectedGenerator.generator_display_name,
        generator_id: currentlySelectedGenerator.generator_id,
        site_id: currentlySelectedGenerator.site_id,
        metering_or_market_location_id:
          currentlySelectedGenerator.metering_or_market_location_id ?? null,
        metering_or_market_location_display_name:
          currentlySelectedGenerator.metering_or_market_location_display_name ??
          "",
        weighting: currentlySelectedGenerator.weighting ?? null
      });

    table.setCreatingRow(prefilledCreatingRow);
  }, [tableIsCreating, table, currentlySelectedGenerator]);

  const namesOfGeneratorSharesToBeDeleted = generatorSharesToBeDeleted.map(
    (generatorShare) => generatorShare.generator_display_name
  );
  const denOrDie =
    namesOfGeneratorSharesToBeDeleted.length === 1 ? "den" : "die";

  return (
    <LoadOrError
      error={error || personError}
      loading={isLoading || isLoadingPerson}
      loadingMessage="Erzeugerdaten werden geladen ..."
    >
      <MantineReactTable table={table} />
      <MultiConfirmationModal
        actionName="aus dem Vertrag entfernen"
        actionObjects={namesOfGeneratorSharesToBeDeleted}
        confirmationText={`Möchten Sie ${denOrDie} folgenden Erzeuger wirklich aus dem Vertrag entfernen?`}
        isModalOpen={generatorSharesToBeDeleted.length > 0}
        objectName="Erzeuger"
        toggleModal={() => setGeneratorSharesToBeDeleted([])}
        onAction={async () => {
          setIsDeleting(true);

          await Promise.all(
            generatorSharesToBeDeleted.map((generatorShare) =>
              onDelete(generatorShare.generator_id)
            )
          );

          setGeneratorSharesToBeDeleted([]);
          table.setRowSelection({}); // MRT doesn't auto-clear the selected rows after deletion
          setIsDeleting(false);
        }}
      />
    </LoadOrError>
  );
}

export {
  DirektvermarktungGeneratorShareTable,
  DirektvermarktungGeneratorShareTableProps
};
